import { useState, useEffect, useCallback } from 'react';
import { LTSSService, Alert, FinancialSummary } from '../types/ltss';
import {
  StoredReport,
  TrendDataPoint,
  saveReport,
  getAllReports,
  deleteReport,
  getTrendData,
  generateContentBasedId,
  extractWeekRange,
} from '../utils/indexedDB';

interface UseHistoricalDataReturn {
  reports: StoredReport[];
  trendData: TrendDataPoint[];
  isLoading: boolean;
  error: string | null;
  saveCurrentReport: (
    fileName: string,
    services: LTSSService[],
    alerts: Alert[],
    financials: FinancialSummary,
    staffCount: number,
    clientCount: number
  ) => Promise<void>;
  loadReport: (report: StoredReport) => void;
  removeReport: (id: string) => Promise<void>;
  refreshData: () => Promise<void>;
}

export function useHistoricalData(
  onLoadReport?: (services: LTSSService[], fileName: string) => void
): UseHistoricalDataReturn {
  const [reports, setReports] = useState<StoredReport[]>([]);
  const [trendData, setTrendData] = useState<TrendDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [allReports, trends] = await Promise.all([
        getAllReports(),
        getTrendData(),
      ]);
      setReports(allReports);
      setTrendData(trends);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load historical data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const saveCurrentReport = useCallback(async (
    fileName: string,
    services: LTSSService[],
    alerts: Alert[],
    financials: FinancialSummary,
    staffCount: number,
    clientCount: number
  ) => {
    const criticalAlerts = alerts.filter(a => a.type === 'CRITICAL').length;
    
    const report: StoredReport = {
      id: generateContentBasedId(services),
      fileName,
      uploadDate: new Date(),
      weekRange: extractWeekRange(services),
      serviceCount: services.length,
      services,
      alerts,
      financials,
      summary: {
        totalRevenue: financials.totalAmountPaid,
        totalAlerts: alerts.length,
        criticalAlerts,
        staffCount,
        clientCount,
      },
    };

    await saveReport(report);
    await refreshData();
  }, [refreshData]);

  const loadReport = useCallback((report: StoredReport) => {
    if (onLoadReport) {
      onLoadReport(report.services, report.fileName);
    }
  }, [onLoadReport]);

  const removeReport = useCallback(async (id: string) => {
    await deleteReport(id);
    await refreshData();
  }, [refreshData]);

  return {
    reports,
    trendData,
    isLoading,
    error,
    saveCurrentReport,
    loadReport,
    removeReport,
    refreshData,
  };
}
